import React, { useState } from 'react'
import { Euro, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { fmtEur } from './TransportSinistreBlocks'

const API = process.env.REACT_APP_BACKEND_URL
const STATUTS = { prevu: 'Prevu', paye: 'Paye', annule: 'Annule' }
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '--'

export default function TransportReglementsBlock({ sinistre, sinistreId, data, onRefresh, userId }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ montant: '', nature: 'principal', statut: 'paye', date_reglement: new Date().toISOString().slice(0, 10), beneficiaire: '', reference: '' })
  const [saving, setSaving] = useState(false)
  const regs = data?.reglements || []
  const provision = Number(sinistre.provision_compagnie) || 0
  const franchise = Number(sinistre.franchise_calculee) || 0
  const nette = sinistre.polices?.provision_nette_de_franchise ? provision : Math.max(0, provision - franchise)
  const recoursAss = Number(sinistre.recours_encaisse) || 0
  const chargeAssureur = Math.max(0, nette - recoursAss)

  async function submit(e) {
    e.preventDefault()
    if (!form.montant) { toast.error('Montant requis'); return }
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/sinistres/${sinistreId}/reglements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, montant: parseFloat(form.montant), beneficiaire: form.beneficiaire || null, reference: form.reference || null, user_id: userId }),
      })
      const d = await res.json()
      if (!res.ok) { toast.error(d.detail || 'Erreur'); return }
      toast.success('Reglement enregistre'); setOpen(false); setForm(f => ({ ...f, montant: '', reference: '' })); onRefresh?.()
    } catch { toast.error('Erreur reseau') } finally { setSaving(false) }
  }

  async function remove(id) {
    if (!window.confirm('Supprimer ce reglement ?')) return
    const res = await fetch(`${API}/api/sinistres/${sinistreId}/reglements/${id}`, { method: 'DELETE' })
    if (res.ok) { toast.success('Reglement supprime'); onRefresh?.() } else toast.error('Erreur')
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="transport-reglements-block">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Euro size={14} className="text-emerald-600" /> Franchise & reglements</h2>
        <button onClick={() => setOpen(o => !o)} className="text-xs px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1" data-testid="btn-add-reglement"><Plus size={12} /> Reglement</button>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-4">
          <Kpi label="Provision compagnie" value={fmtEur(provision)} testid="kpi-provision" />
          <Kpi label="Franchise calculee" value={fmtEur(franchise)} sub={sinistre.polices?.provision_nette_de_franchise ? 'provision deja nette' : 'a charge client'} testid="kpi-franchise" />
          <Kpi label="Recours assureur encaisses" value={fmtEur(recoursAss)} testid="kpi-recours-assureur" />
          <Kpi label="Net assureur" value={fmtEur(chargeAssureur)} strong testid="kpi-net-assureur" sub="avant ecretement S/P" />
          <Kpi label="Regle (paye)" value={fmtEur(sinistre.montant_regle_total)} sub={`principal ${fmtEur(sinistre.montant_regle_principal)} · frais ${fmtEur(sinistre.montant_regle_frais)}`} testid="kpi-regle" />
        </div>
        {open && (
          <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs bg-emerald-50/40 rounded-lg p-3 mb-3" data-testid="form-reglement">
            <input type="number" step="0.01" placeholder="Montant" value={form.montant} onChange={e => setForm({ ...form, montant: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded" data-testid="input-reglement-montant" />
            <select value={form.nature} onChange={e => setForm({ ...form, nature: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded bg-white"><option value="principal">Principal</option><option value="frais">Frais</option></select>
            <select value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded bg-white">{Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <input type="date" value={form.date_reglement} onChange={e => setForm({ ...form, date_reglement: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded" />
            <input placeholder="Beneficiaire" value={form.beneficiaire} onChange={e => setForm({ ...form, beneficiaire: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded" />
            <button type="submit" disabled={saving} className="px-2 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-50" data-testid="submit-reglement">Ajouter</button>
          </form>
        )}
        {regs.length === 0 ? <p className="text-xs text-slate-400">Aucun reglement enregistre</p> : (
          <table className="w-full text-xs" data-testid="table-reglements">
            <thead><tr className="text-slate-400 text-left"><th className="py-1">Date</th><th>Nature</th><th>Beneficiaire</th><th>Statut</th><th className="text-right">Montant</th><th /></tr></thead>
            <tbody>
              {regs.map(r => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="py-1.5">{fmtDate(r.date_reglement)}</td><td className="capitalize">{r.nature}</td><td>{r.beneficiaire || '--'}</td>
                  <td><span className={`px-1.5 py-0.5 rounded ${r.statut === 'paye' ? 'bg-emerald-100 text-emerald-700' : r.statut === 'annule' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`}>{STATUTS[r.statut]}</span></td>
                  <td className="text-right font-medium">{fmtEur(r.montant)}</td>
                  <td className="text-right"><button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`del-reglement-${r.id}`}><Trash2 size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Kpi({ label, value, sub, strong, testid }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2" data-testid={testid}>
      <p className="text-[11px] text-slate-400">{label}</p>
      <p className={`${strong ? 'text-emerald-700 font-bold' : 'text-slate-800 font-medium'}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  )
}
